/**
 * Chat CRUD — Phase 5d replacement.
 * Delegates to /api/chats/* endpoints on dash-api.
 */

const BASE = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';

function authHeaders() {
    const token = localStorage.getItem('access_token');
    return token
        ? {'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json'}
        : {'Content-Type': 'application/json'};
}

async function apiFetch(path, opts = {}) {
    const resp = await fetch(`${BASE}${path}`, {headers: authHeaders(), ...opts});
    if (resp.status === 404) return null;
    if (!resp.ok) throw new Error(`${opts.method || 'GET'} ${path} → ${resp.status}`);
    if (resp.status === 204) return null;
    return resp.json();
}

export const createChat = async ({participants = [], participantMeta = [], initialMessage = null} = {}) => {
    const data = await apiFetch('/api/chats', {
        method: 'POST',
        body: JSON.stringify({participants, participantMeta, initialMessage}),
    });
    return data;
};

export const findOneOnOneChat = async (userA, userB) => {
    if (!userA || !userB) return null;
    return apiFetch(
        `/api/chats/one-on-one?userA=${encodeURIComponent(userA)}&userB=${encodeURIComponent(userB)}`
    );
};

/** Resolve a chat participant id from a user object or raw string. */
export const extractUid = (u) => {
    if (u == null) return null;
    if (typeof u === 'string') {
        const trimmed = u.trim();
        return trimmed || null;
    }
    if (typeof u === 'number' && Number.isFinite(u)) return String(u);
    for (const key of ['uid', 'id', 'userId']) {
        const value = u[key];
        if (typeof value === 'string' && value.trim()) return value.trim();
        if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    }
    return null;
};

/** Parent id for chats opened from a Collection assignee profile modal. */
export const collectionAssigneeChatParentId = (assigneeId) => {
    const id = extractUid(assigneeId);
    return id ? `collection-assignee:${id}` : null;
};

export const createOrGetOneOnOneChat = async (userA, userB) => {
    const uidA = extractUid(userA);
    const uidB = extractUid(userB);
    if (!uidA || !uidB) throw new Error('Invalid participants for chat creation');

    const normalize = (u, uid) =>
        typeof u === 'string'
            ? {uid}
            : {...u, uid};

    const data = await apiFetch('/api/chats/one-on-one', {
        method: 'POST',
        body: JSON.stringify({userA: normalize(userA, uidA), userB: normalize(userB, uidB)}),
    });
    return data;
};

export const deleteChat = async (chatId) => {
    if (!chatId) throw new Error('chatId required');
    return apiFetch(`/api/chats/${encodeURIComponent(chatId)}`, {method: 'DELETE'});
};

export const sendMessageToChat = async (chatId, payload = {}) => {
    if (!chatId) throw new Error('chatId required');
    if (!payload?.text) throw new Error('message text required');
    return apiFetch(`/api/chats/${encodeURIComponent(chatId)}/messages`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
};
